import { useState, useRef, useCallback, useEffect } from 'react';
import {
  processImageForUpload,
  isValidImageType,
  createImagePreview,
  revokeImagePreview,
} from '../../utils/imageUtils';

interface DiagnosisUploadProps {
  onImageReady: (base64: string) => void;
  onError: (message: string) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Editorial photo upload for plant diagnosis
 */
export function DiagnosisUpload({
  onImageReady,
  onError,
  disabled = false,
  className = '',
}: DiagnosisUploadProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        revokeImagePreview(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFile = useCallback(
    async (file: File) => {
      if (!isValidImageType(file)) {
        onError('Please select a valid image file (JPEG, PNG, or WebP)');
        return;
      }

      // Clear previous preview
      if (previewUrl) {
        revokeImagePreview(previewUrl);
      }

      // Show preview immediately
      const preview = createImagePreview(file);
      setPreviewUrl(preview);

      // Process image
      setProcessing(true);
      try {
        const { base64, sizeKB } = await processImageForUpload(file);

        if (sizeKB > 500) {
          onError('Image is too large. Please try a smaller photo.');
          return;
        }

        onImageReady(base64);
      } catch (err) {
        console.error('Image processing error:', err);
        onError('Failed to process image. Please try again.');
      } finally {
        setProcessing(false);
      }
    },
    [onImageReady, onError, previewUrl]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
      // Reset input so the same file can be selected again
      e.target.value = '';
    },
    [handleFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (disabled || processing) return;

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFile(file);
      }
    },
    [disabled, processing, handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const clearPreview = useCallback(() => {
    if (previewUrl) {
      revokeImagePreview(previewUrl);
      setPreviewUrl(null);
    }
  }, [previewUrl]);

  return (
    <div className={className}>
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
        aria-hidden="true"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
        aria-hidden="true"
      />

      {/* Upload area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          disabled || processing
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : 'border-gray-300 hover:border-editorial-gold bg-white cursor-pointer'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        role="button"
        tabIndex={disabled || processing ? -1 : 0}
        aria-label="Upload or drop a photo of your plant"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
      >
        {previewUrl ? (
          <div className="space-y-4">
            <div className="relative inline-block">
              <img
                src={previewUrl}
                alt="Preview of selected plant photo"
                className="max-h-48 mx-auto rounded-lg shadow-sm"
              />
              {processing && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span
                      className="material-symbols-outlined text-primary animate-pulse"
                      style={{ fontSize: 20 }}
                      aria-hidden="true"
                    >
                      hourglass_top
                    </span>
                    <span className="text-sm font-sans text-gray-500">Processing...</span>
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                clearPreview();
              }}
              className="text-sm font-sans text-gray-400 hover:text-danger transition-colors"
              disabled={processing}
            >
              Remove photo
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <span
              className="material-symbols-outlined text-gray-300 block"
              style={{ fontSize: 40 }}
              aria-hidden="true"
            >
              photo_camera
            </span>
            <div>
              <p className="font-display font-bold text-editorial-black">Add a photo</p>
              <p className="text-sm font-serif text-gray-400 mt-1">
                Capture the affected area for best results
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      {!previewUrl && (
        <div className="flex gap-3 mt-4">
          <button
            type="button"
            onClick={() => cameraInputRef.current?.click()}
            disabled={disabled || processing}
            className="flex-1 h-12 flex items-center justify-center gap-2 bg-editorial-black text-white rounded-lg font-sans font-bold text-xs uppercase tracking-widest hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Take a photo with camera"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden="true">
              photo_camera
            </span>
            Take Photo
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || processing}
            className="flex-1 h-12 flex items-center justify-center gap-2 border border-gray-300 text-editorial-black rounded-lg font-sans font-bold text-xs uppercase tracking-widest hover:border-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Choose a photo from library"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }} aria-hidden="true">
              photo_library
            </span>
            Choose Photo
          </button>
        </div>
      )}

      {/* Processing indicator */}
      {processing && (
        <div role="status" aria-live="polite" className="mt-4 text-center">
          <span className="sr-only">Processing image...</span>
        </div>
      )}
    </div>
  );
}
